import {
  useGetPostListFullQuery,
  useGetTagListFullQuery,
  type Tag,
} from "@package/ui-gql/index-data-access";
import { produce } from "immer";
import { get } from "lodash";
import type { PropsWithChildren } from "react";
import { useCallback, useEffect, useState } from "react";
import { type FlyoutProps } from "../../../utils/flyout-props";
import {
  defaultFlyoutSearchContextState,
  FlyoutSearchStateContext,
} from "./context-flyout-search";
import { getSearchDisplayType } from "./context-flyout-search-utils";

type FlyoutSearchStateProviderProps = {
  screen: FlyoutProps["screen"];
};

export function FlyoutSearchStateProvider({
  screen,
  children,
}: PropsWithChildren<FlyoutSearchStateProviderProps>) {
  const [state, setState] = useState(defaultFlyoutSearchContextState);

  const hideKeyboard = get(
    screen?.flyout?.post,
    "customData.search.hideKeyboard",
  );
  const headerMedia = get(
    screen?.flyout?.post,
    "customData.search.headerMedia",
  );
  const text = get(screen?.flyout?.post, "customData.search.text");
  const tags = get(screen?.flyout?.post, "customData.search.tags");
  const tagsArray = Array.isArray(tags) ? tags : [];
  const tagSelectionEnabled = get(
    screen?.flyout?.post,
    "customData.search.tagSelectionEnabled",
  );
  const large = get(screen?.flyout?.post, "customData.search.size");
  const backgroundColorPrimary =
    screen.flyout?.post?.customData?.primary?.backgroundColor;
  const backgroundColorSecondary =
    screen.flyout?.post?.customData?.secondary?.backgroundColor;
  const colorPrimary = screen.flyout?.post?.customData?.primary?.color;
  const colorSecondary = screen.flyout?.post?.customData?.secondary?.color;
  const onSelectTag = useCallback((tag: Tag) => {
    setState((prev) =>
      produce(prev, (draft) => {
        draft.tagSelected = tag;
        draft.displayType = getSearchDisplayType(draft);
      }),
    );
  }, []);
  const onCloseTag = useCallback(() => {
    setState((prev) =>
      produce(prev, (draft) => {
        draft.tagSelected = undefined;
        draft.displayType = getSearchDisplayType(draft);
      }),
    );
  }, []);
  const {
    data: dataTags,
    loading: loadingTags,
    error: errorTags,
  } = useGetTagListFullQuery({
    variables: {
      filter: {
        ids: tagsArray,
      },
      page: 0,
      perPage: 1000,
    },
    skip: tagsArray.length === 0,
  });
  const {
    data: dataPosts,
    loading: loadingPosts,
    error: errorPosts,
  } = useGetPostListFullQuery({
    variables: {
      filter: {
        enabled: true,
        hideFromSearch: false,
        tags: state.tagSelected?.id
          ? [state.tagSelected.id]
          : tagsArray.length > 0
            ? tagsArray
            : undefined,
        q: state.text ?? undefined,
      },
      page: 0,
      perPage: 1000,
    },
    fetchPolicy: state.text ? "network-only" : "cache-first",
    skip:
      state.displayType !== "posts" ||
      (!state.tagSelected && state.tags.length === 0),
  });

  useEffect(
    function effectOnCustomDataChanged() {
      setState((prev) =>
        produce(prev, (draft) => {
          draft.backgroundColorPrimary =
            backgroundColorPrimary ??
            defaultFlyoutSearchContextState.backgroundColorPrimary;
          draft.backgroundColorSecondary =
            backgroundColorSecondary ??
            defaultFlyoutSearchContextState.backgroundColorSecondary;
          draft.colorPrimary =
            colorPrimary ?? defaultFlyoutSearchContextState.colorPrimary;
          draft.colorSecondary =
            colorSecondary ?? defaultFlyoutSearchContextState.colorSecondary;
          draft.hideKeyboard =
            typeof hideKeyboard === "boolean"
              ? hideKeyboard
              : defaultFlyoutSearchContextState.hideKeyboard;
          draft.headerMedia = headerMedia ?? undefined;
          draft.text = text ?? undefined;
          draft.tagSelectionEnabled =
            typeof tagSelectionEnabled === "boolean"
              ? tagSelectionEnabled
              : defaultFlyoutSearchContextState.tagSelectionEnabled;
          draft.large =
            typeof large === "boolean"
              ? large
              : defaultFlyoutSearchContextState.large;
          draft.screen = screen;
          draft.displayType = getSearchDisplayType(draft);
        }),
      );
    },
    [
      backgroundColorPrimary,
      backgroundColorSecondary,
      colorPrimary,
      colorSecondary,
      headerMedia,
      hideKeyboard,
      large,
      screen,
      tagSelectionEnabled,
      tags,
      text,
    ],
  );

  useEffect(
    function effectOnLoadedDataChanged() {
      setState((prev) =>
        produce(prev, (draft) => {
          draft.loading = loadingTags || loadingPosts;
          draft.error = !!(errorTags || errorPosts);
          draft.tags = dataTags?.items ?? [];
          draft.posts = dataPosts?.items ?? [];
        }),
      );
    },
    [
      dataPosts?.items,
      dataTags?.items,
      errorPosts,
      errorTags,
      loadingPosts,
      loadingTags,
    ],
  );

  useEffect(
    function effectOnSearchLoaded() {
      setState((prev) =>
        produce(prev, (draft) => {
          draft.onSelectTag = onSelectTag;
          draft.onCloseTag = onCloseTag;
        }),
      );
    },
    [onCloseTag, onSelectTag],
  );

  return (
    <FlyoutSearchStateContext.Provider value={state}>
      {children}
    </FlyoutSearchStateContext.Provider>
  );
}
